import { types, clone } from 'mobx-state-tree';
import OETRow, { compareSiblingRows, type IOETRow } from './oetrow';
import OETColumn, { type IOETColumn } from './oetcolumn';
import type { IObjectType } from '../metamodel/object_type';

/**
 * The OET instance interface.
 *
 * HAND-DECLARED (not `Instance<typeof OET>`) because it is the OET container:
 * `OETRow` reaches it back through `getParentOfType(self, OET)`. Typing that
 * lookup against this interface lets TS resolve it via the interface instead of
 * re-inferring `typeof OET` (which embeds `types.array(OETRow)` and would loop
 * back into OETRow's own initializer).
 */
export interface IOET {
  readonly rows: IOETRow[];
  readonly columns: IOETColumn[];
  readonly empty: boolean;
  findColumnIndexByObjectType(ot: IObjectType): number;
  readonly json: Record<string, string>[];
  readonly columnNames: string[];
  readonly flattenedRows: IOETRow[];
  addColumn(object: string): void;
  addRow(event: string): void;
  addSubRow(parentEvent: string, event: string): void;
  removeColumn(object: string): void;
  removeRow(event: string): IOETRow;
  removeSubRow(parentEvent: string, event: string): IOETRow;
  moveRowToRoot(parentEvent: string, event: string): void;
  moveRowToParent(parentEvent: string, event: string): void;
  moveRow(from: number, to: number): void;
  moveColumn(from: number, to: number): void;
  sortRows(): void;
  sortColumns(): void;
  sortColumnsBUL(): void;
  sortColumnsTDL(): void;
}

/**
 * The OET in a MERODE ui model.
 */
const OET = types
  .model('OET', {
    // array of rows in the oet
    rows: types.optional(types.array(OETRow), []),
    // array of columns in the oet
    columns: types.optional(types.array(OETColumn), []),
  })
  .views((self0) => {
    const self = self0 as unknown as IOET;
    return {
      // is the oet empty?
      get empty() {
        return self0.rows.length === 0 && self0.columns.length === 0;
      },
      // find the index of the column for an object type
      findColumnIndexByObjectType(ot: IObjectType) {
        return self0.columns.findIndex(
          (c) => c.object === (ot as unknown as typeof c.object)
        );
      },
      // get a json respresentation of the oet, including (a textual representation
      // of) the content to facilitate export to csv
      get json(): Record<string, string>[] {
        return self.flattenedRows.map((r) => {
          let prefixes: string[] = [];
          if (r.event.specialised) {
            prefixes = Array(r.event.specialisationLevel);
            prefixes.fill('→ ');
          }
          const row: Record<string, string> = {
            event: `${prefixes.join('')}${r.event.name}`,
          };
          self0.columns.forEach((c) => {
            const methods = c.object.methods.filter((m) => m.ownerEventType === r.event);
            row[c.object.name] = methods
              .map((m) => `${m.metadata}${m.secondary ? ' (s)' : ''}${m.hidden ? ' (h)' : ''}`)
              .join('\n');
          });
          return row;
        });
      },
      // get the names of the columns (i.e. the names of the object types for each
      // column)
      get columnNames() {
        return self0.columns.map((c) => c.object.name);
      },
      get flattenedRows(): IOETRow[] {
        return self0.rows.flatMap((r) => r.flattenedRows.slice());
      },
    };
  })
  .actions((self0) => {
    const self = self0 as unknown as IOET;
    return {
      // object is an id! We need this to be serializable to allow replay.
      addColumn(object: string) {
        self0.columns.push({
          object,
        } as unknown as (typeof self0.columns)[number]);
      },
      // event is an id! We need this to be serializable to allow replay.
      addRow(event: string) {
        self0.rows.push({
          event,
        } as unknown as (typeof self0.rows)[number]);
      },
      // parentEvent and event are ids! We need this to be serializable to allow replay.
      addSubRow(parentEvent: string, event: string) {
        const parent = self.flattenedRows.find((r) => r.event.id === parentEvent);
        parent!.addSubRow(event);
      },
      // object is an id! We need this to be serializable to allow replay.
      removeColumn(object: string) {
        const col = self0.columns.find((c) => c.object.id === object);
        self0.columns.remove(col as unknown as (typeof self0.columns)[number]);
      },
      // event is an id! We need this to be serializable to allow replay.
      removeRow(event: string) {
        const row = self0.rows.find((r) => r.event.id === event);
        const cloned = clone(row as unknown as (typeof self0.rows)[number]);
        self0.rows.remove(row as unknown as (typeof self0.rows)[number]);
        return cloned as unknown as IOETRow;
      },
      // parentEvent and event are ids! We need this to be serializable to allow replay.
      removeSubRow(parentEvent: string, event: string) {
        const parent = self.flattenedRows.find((r) => r.event.id === parentEvent);
        return parent!.removeSubRow(event);
      },
      // parentEvent and event are ids! We need this to be serializable to allow replay.
      moveRowToRoot(parentEvent: string, event: string) {
        const row = self.removeSubRow(parentEvent, event);
        self0.rows.push(row as unknown as (typeof self0.rows)[number]);
      },
      // parentEvent and event are ids! We need this to be serializable to allow replay.
      moveRowToParent(parentEvent: string, event: string) {
        const row = self.removeRow(event);
        const parent = self.flattenedRows.find((r) => r.event.id === parentEvent);
        // Parent could be undefined while adding a looping inheritance
        if (parent != null) {
          parent.subRows.push(row);
        }
      },
      // move a row from index 'from' to index 'to'
      moveRow(from: number, to: number) {
        const row = self0.rows[from];
        const newRow = clone(row);
        self0.rows.splice(to, 0, newRow);
        self0.rows.remove(row);
      },
      // move a column from index 'from' to index 'to'
      moveColumn(from: number, to: number) {
        const column = self0.columns[from];
        const newColumn = clone(column);
        self0.columns.splice(to, 0, newColumn);
        self0.columns.remove(column);
      },
      /**
       * Sort the rows based on the column index of the owning object type for the event type in the
       * row. For the rows with same owning object type, sort based on method type of the owning
       * method for the event type in the row in this order: CREATE, MODIFY, END. Finally sort
       * alphabetically on event type name (lowercased).
       *
       * Specialised event types are sorted directly below their ownerEventType in the order of the
       * column index of the object type for the specialised method that corresponds to it.
       */
      sortRows() {
        self0.rows = self0.rows.slice().sort((first, second) => {
          return compareSiblingRows(
            first as unknown as IOETRow,
            second as unknown as IOETRow
          );
        }) as unknown as typeof self0.rows;
        self0.rows.forEach((r) => r.sortSubRows());
      },
      // Sort columns alphabetically on object type name (lowercased)
      sortColumns() {
        self0.columns = self0.columns.slice().sort((first, second) => {
          return first.lowerCaseObjectName >= second.lowerCaseObjectName ? 1 : -1;
        }) as unknown as typeof self0.columns;
      },
      // Sort columns based on the bottom-up level of the object type
      sortColumnsBUL() {
        self0.columns = self0.columns.slice().sort((first, second) => {
          if (first.object.bul === second.object.bul) {
            return first.lowerCaseObjectName >= second.lowerCaseObjectName ? 1 : -1;
          } else {
            return first.object.bul - second.object.bul;
          }
        }) as unknown as typeof self0.columns;
      },
      // Sort columns based on the top-down level of the object type
      sortColumnsTDL() {
        self0.columns = self0.columns.slice().sort((first, second) => {
          if (first.object.tdl === second.object.tdl) {
            return first.lowerCaseObjectName >= second.lowerCaseObjectName ? 1 : -1;
          } else {
            return first.object.tdl - second.object.tdl;
          }
        }) as unknown as typeof self0.columns;
      },
    };
  });

export default OET;
